import type { Zone } from '../../services/zonesApi';
import { formatEvidenceTimestampLocal } from './formatEvidenceTimestamp';
import { zoneOverlayPolygon } from './zoneOverlayGeometry';

export interface EvidenceSnapshotOverlayOpts {
  label: string;
  ts: string;
  zone_id?: string;
  camera_id?: string;
  includeHeader?: boolean;
}

const ZONE_STROKE = 'rgba(0, 200, 83, 0.95)';
const ZONE_FILL = 'rgba(0, 200, 83, 0.18)';
const HEADER_BG = 'rgba(0,0,0,0.72)';
const HEADER_TEXT = 'rgba(255,255,255,0.98)';

export function findZoneForEvidenceOverlay(
  zones: Zone[],
  zoneId: string,
  cameraId?: string,
): Zone | null {
  const normalizedZoneId = String(zoneId || '').trim();
  const normalizedCameraId = String(cameraId || '').trim();
  if (!normalizedZoneId || !Array.isArray(zones) || zones.length === 0) return null;

  return (
    zones.find(
      (zone) =>
        String(zone.zone_id || '').trim() === normalizedZoneId &&
        (!normalizedCameraId || !zone.camera_id || String(zone.camera_id).trim() === normalizedCameraId),
    ) ||
    zones.find((zone) => String(zone.name || '').trim() === normalizedZoneId) ||
    zones.find((zone) => String(zone.zone_id || '').trim() === normalizedZoneId) ||
    null
  );
}

export async function overlayEvidenceSnapshotBlob(
  blob: Blob,
  zones: Zone[],
  opts: EvidenceSnapshotOverlayOpts,
): Promise<Blob> {
  const url = URL.createObjectURL(blob);
  try {
    const img = new Image();
    img.crossOrigin = 'anonymous';
    await new Promise<void>((resolve, reject) => {
      img.onload = () => resolve();
      img.onerror = () => reject(new Error('image_load_failed'));
      img.src = url;
    });

    const width = img.naturalWidth || img.width;
    const height = img.naturalHeight || img.height;
    const canvas = document.createElement('canvas');
    canvas.width = width;
    canvas.height = height;
    const ctx = canvas.getContext('2d');
    if (!ctx) return blob;

    ctx.drawImage(img, 0, 0, width, height);

    const zone = findZoneForEvidenceOverlay(zones, opts.zone_id || '', opts.camera_id);
    let polygon: number[][] | null = null;
    if (zone) {
      polygon = zoneOverlayPolygon(zone);
    }

    ctx.save();
    ctx.lineWidth = Math.max(3, Math.round(Math.min(width, height) * 0.006));
    ctx.strokeStyle = ZONE_STROKE;
    ctx.fillStyle = ZONE_FILL;
    if (polygon && polygon.length > 2) {
      ctx.beginPath();
      for (let i = 0; i < polygon.length; i += 1) {
        const point = polygon[i];
        const x = Math.max(0, Math.min(1, Number(point?.[0] ?? 0))) * width;
        const y = Math.max(0, Math.min(1, Number(point?.[1] ?? 0))) * height;
        if (i === 0) ctx.moveTo(x, y);
        else ctx.lineTo(x, y);
      }
      ctx.closePath();
      ctx.fill();
      ctx.stroke();
    }
    ctx.restore();

    const label = String(opts.label || '').trim();
    const stamp = formatEvidenceTimestampLocal(opts.ts) || '';
    const text = [label, stamp].filter(Boolean).join('  •  ');
    if (text && opts.includeHeader !== false) {
      const pad = Math.max(10, Math.round(Math.min(width, height) * 0.012));
      const fontSize = Math.max(18, Math.round(Math.min(width, height) * 0.028));
      ctx.save();
      ctx.font = `700 ${fontSize}px system-ui, -apple-system, Segoe UI, Roboto, Arial`;
      const metrics = ctx.measureText(text);
      const boxWidth = Math.min(width - 2 * pad, metrics.width + 2 * pad);
      const boxHeight = Math.round(fontSize * 1.45);
      ctx.fillStyle = HEADER_BG;
      ctx.fillRect(pad, pad, boxWidth, boxHeight);
      ctx.fillStyle = HEADER_TEXT;
      ctx.textBaseline = 'middle';
      ctx.fillText(text, pad + Math.round(pad * 0.7), pad + Math.round(boxHeight / 2));
      ctx.restore();
    }

    const output = await new Promise<Blob | null>((resolve) => {
      canvas.toBlob((value) => resolve(value), 'image/jpeg', 0.92);
    });
    return output || blob;
  } catch {
    return blob;
  } finally {
    URL.revokeObjectURL(url);
  }
}
